import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'

import { formatDate, toDateInputValue } from '../common/date'
import { useI18n } from '../contexts/I18nContext'

interface DatePickerProps {
  value: string
  onChange: (value: string) => void
  required?: boolean
  disabled?: boolean
  className?: string
  ariaLabel?: string
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/
const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function parseDateOnly(value: string): Date {
  if (!DATE_ONLY.test(value)) {
    return new Date()
  }
  const [year, month, day] = value.split('-').map(Number)
  return new Date(year, month - 1, day)
}

function monthStart(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function buildCalendarDays(month: Date): Date[] {
  const firstDay = monthStart(month)
  const gridStart = new Date(firstDay)
  gridStart.setDate(firstDay.getDate() - firstDay.getDay())

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart)
    date.setDate(gridStart.getDate() + index)
    return date
  })
}

function sameDate(left: Date, right: Date): boolean {
  return left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate()
}

function shiftMonth(date: Date, amount: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1)
}

export default function DatePicker({
  value,
  onChange,
  required = false,
  disabled = false,
  className,
  ariaLabel,
}: DatePickerProps) {
  const { locale, t } = useI18n()
  const selectedDate = parseDateOnly(value)
  const [isOpen, setIsOpen] = useState(false)
  const [visibleMonth, setVisibleMonth] = useState(monthStart(selectedDate))

  const calendarDays = useMemo(() => buildCalendarDays(visibleMonth), [visibleMonth])
  const today = new Date()
  const displayValue = value ? formatDate(value) : required ? formatDate(today) : ''
  const monthLabel = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(visibleMonth)

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isOpen])

  function selectDate(date: Date): void {
    onChange(toDateInputValue(date))
    setIsOpen(false)
  }

  function openPicker(): void {
    setVisibleMonth(monthStart(selectedDate))
    setIsOpen(true)
  }

  return (
    <>
      <button
        type="button"
        className={['field__input', 'date-picker__trigger', className].filter(Boolean).join(' ')}
        onClick={openPicker}
        disabled={disabled}
        aria-label={ariaLabel ?? t('Date')}
      >
        <span className={displayValue ? undefined : 'date-picker__placeholder'}>{displayValue || 'd-MMM-yy'}</span>
        <CalendarDaysIcon />
      </button>

      {isOpen && createPortal(
        <>
          <div className="modal-backdrop" onClick={() => setIsOpen(false)} />
          <div className="modal-wrap">
            <div className="modal modal--narrow date-picker__modal">
              <div className="modal__header date-picker__header">
                <button
                  type="button"
                  className="date-picker__nav"
                  onClick={() => setVisibleMonth((current) => shiftMonth(current, -1))}
                  aria-label={t('Previous month')}
                >
                  <ChevronLeftIcon />
                </button>
                <h3>{monthLabel}</h3>
                <button
                  type="button"
                  className="date-picker__nav"
                  onClick={() => setVisibleMonth((current) => shiftMonth(current, 1))}
                  aria-label={t('Next month')}
                >
                  <ChevronRightIcon />
                </button>
                <button type="button" onClick={() => setIsOpen(false)} aria-label={t('Close date picker')}>
                  <XMarkIcon />
                </button>
              </div>

              <div className="modal__body date-picker__body">
                <div className="date-picker__weekdays">
                  {WEEKDAY_LABELS.map((weekday) => (
                    <span key={weekday}>{weekday}</span>
                  ))}
                </div>
                <div className="date-picker__grid">
                  {calendarDays.map((date) => {
                    const dateValue = toDateInputValue(date)
                    const isCurrentMonth = date.getMonth() === visibleMonth.getMonth()
                    const isSelected = value ? sameDate(date, selectedDate) : false
                    const isToday = sameDate(date, today)

                    return (
                      <button
                        key={dateValue}
                        type="button"
                        className={[
                          'date-picker__day',
                          date.getDay() === 0 ? 'date-picker__day--sunday' : undefined,
                          date.getDay() === 6 ? 'date-picker__day--saturday' : undefined,
                          isCurrentMonth ? undefined : 'date-picker__day--muted',
                          isSelected ? 'date-picker__day--selected' : undefined,
                          isToday ? 'date-picker__day--today' : undefined,
                        ].filter(Boolean).join(' ')}
                        onClick={() => selectDate(date)}
                      >
                        {date.getDate()}
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="modal__actions date-picker__actions">
                <button type="button" className="btn btn--ghost" onClick={() => setIsOpen(false)}>
                  {t('Cancel')}
                </button>
                <button type="button" className="btn btn--primary" onClick={() => selectDate(today)}>
                  {t('Today')}
                </button>
              </div>
            </div>
          </div>
        </>,
        document.body,
      )}
    </>
  )
}